import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  ChevronUp,
  Edit,
  Copy,
  Trash
} from "lucide-react";
import { useState } from "react";
import { DayActivity } from "../models/activity.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";

interface BaseActivityCardProps {
  activity: DayActivity;
  onEdit: (activity: DayActivity) => void;
  onDelete: (activity: DayActivity) => void;
  onDuplicate?: (activity: DayActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  children: {
    alwaysVisible: React.ReactNode;
    collapsible: React.ReactNode;
  };
}

export const BaseActivityCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  children
}: BaseActivityCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="w-full border border-gray-200 rounded-2xl p-4 bg-white shadow-sm relative">
        {/* Header with icon, title and menu */}
        <div className="flex items-center gap-3 flex-1">
          {/* Activity Type Icon */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gray-200`}>
            {getActivityTypeIcon(activity.type)}
          </div>

          {/* Title */}
          <h4 className="text-lg font-semibold text-gray-900 truncate flex-1">
            {activity.title} { activity.type === 'accommodationIn' ? '(Check-in)' : activity.type === 'accommodationOut' ? '(Check-out)' : '' }
          </h4>

          {/* Right side - Menu button - Only show if not read-only */}
          {!isReadOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-12 h-12 rounded-full hover:bg-gray-100"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => onEdit(activity)} className="flex items-center gap-2">
                  <Edit className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                {onDuplicate && (
                  <DropdownMenuItem onClick={() => onDuplicate(activity)} className="flex items-center gap-2">
                    <Copy className="h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => onDelete(activity)}
                  className="flex items-center gap-2 text-red-600 focus:text-red-600"
                >
                  <Trash className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Activity Details - Always visible */}
        <div className="flex items-center gap-8 my-4 text-sm text-gray-500">
          {children.alwaysVisible}
        </div>

        {/* Collapsible Content */}
        <CollapsibleContent className="space-y-4">
          {children.collapsible}
        </CollapsibleContent>

        {/* Expand/Collapse Button */}
        <div className="flex items-center justify-center mt-4">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-2"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Less
                </>
              ) : (
                <>
                  <ChevronUp className="h-4 w-4 rotate-180" />
                  More
                </>
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
    </Collapsible>
  );
};
